import React, { useState, useRef, useEffect } from 'react';
import { apiErrorMessage } from '@/lib/apiError';
import {
    Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Phone, Pencil, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import logger from '@/lib/logger';

/**
 * Inline editor for a lead's secondary phone — opens a popover with an
 * input + save / delete actions so agents can capture a number mid-call
 * without opening the full Lead Detail modal.
 *
 * Variants:
 *  - mode="add"   → renders a tiny "+ 2nd" link
 *  - mode="edit"  → renders the existing pill with a pencil icon
 */
export default function InlineSecondaryPhoneEditor({
    leadId,
    value,
    onUpdated,
    primaryPhone,
    mode = 'edit', // 'edit' | 'add'
    className = '',
}) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setDraft(value || '');
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [open, value]);

    const stop = (e) => { e.stopPropagation?.(); e.preventDefault?.(); };

    const save = async (newVal) => {
        const clean = (newVal || '').trim();
        if (clean && clean.replace(/\D/g, '').length < 7) {
            toast.error('Phone must be at least 7 digits');
            return;
        }
        setSaving(true);
        try {
            const res = await apiClient.put(`/leads/${leadId}`, { secondary_phone: clean });
            const updated = res?.data || {};
            const stored = updated.secondary_phone ?? clean;
            toast.success(clean ? 'Secondary number saved' : 'Secondary number removed');
            onUpdated?.(stored);
            setOpen(false);
        } catch (e) {
            logger.error('secondary save failed', e);
            toast.error(apiErrorMessage(e, 'Failed to save secondary number'), { duration: 8000 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                {mode === 'add' ? (
                    <button
                        type="button"
                        onClick={stop}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-dashed border-amber-300 transition-colors ${className}`}
                        data-testid={`lead-card-add-secondary-${leadId}`}
                    >
                        <Plus className="h-3 w-3" />
                        Add 2nd number
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={stop}
                        className={`inline-flex items-center justify-center h-6 w-6 rounded text-amber-600 hover:bg-amber-100 transition-colors ${className}`}
                        title="Edit secondary number"
                        data-testid={`lead-card-edit-secondary-${leadId}`}
                    >
                        <Pencil className="h-3 w-3" />
                    </button>
                )}
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-72 p-3"
                onClick={stop}
                onPointerDown={stop}
                data-testid={`lead-card-secondary-popover-${leadId}`}
            >
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-amber-700">
                        <Phone className="h-3 w-3" />
                        Secondary number
                    </div>
                    {primaryPhone && (
                        <div className="text-[10px] text-muted-foreground">
                            Primary on file: <span className="font-mono">{primaryPhone}</span>
                        </div>
                    )}
                    <form
                        onSubmit={(e) => { e.preventDefault(); save(draft); }}
                        className="space-y-2"
                    >
                        <Input
                            ref={inputRef}
                            type="tel"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="+9715XXXXXXXX"
                            className="h-8 text-sm font-mono"
                            data-testid={`lead-card-secondary-input-${leadId}`}
                        />
                        <div className="flex items-center gap-2">
                            <Button
                                type="submit"
                                size="sm"
                                className="flex-1 h-7 text-xs"
                                disabled={saving}
                                data-testid={`lead-card-secondary-save-${leadId}`}
                            >
                                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                Save
                            </Button>
                            {value ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                    disabled={saving}
                                    onClick={() => save('')}
                                    data-testid={`lead-card-secondary-clear-${leadId}`}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs"
                                    onClick={() => setOpen(false)}
                                >
                                    Cancel
                                </Button>
                            )}
                        </div>
                    </form>
                </div>
            </PopoverContent>
        </Popover>
    );
}
